import * as React from 'react';
import Dialog from 'src/components/common/Dialog';
import Form from 'src/components/controls/forms/Form';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';
import { FormApi, AnyObject } from 'final-form';
import { ODataOptions } from 'src/util/ODataBuilder';
import { FormErrors } from 'src/util/FormErrors';
import PropertyUtil from 'src/util/PropertyUtil';
import { SelectItem } from 'src/models/common/SelectItem';
import FormSelect from '../../controls/forms/redesign/FormFilterSelect';
import FormCheckbox from '../../controls/forms/redesign/FormCheckbox';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Project from 'src/models/Project';
import Client from 'src/models/Client';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import { ProjectAdministratorTypes } from 'src/models/ProjectAdministrationType';
import Landman from 'src/models/Landman';
import ProjectStaff from 'src/models/ProjectStaff';
import MathUtil from 'src/util/MathUtil';

interface IStaffLandmanDialogProps {
    staffToProject: (clientNumber: number, projectNumber: number, user: ProjectStaff) => void;
    projects: PagedResultSet<Project>;
    clients: PagedResultSet<Client>;
    getProjects: (clientNumber: number, options: ODataOptions) => void;
    getProjectAdministration: (clientNumber: number, projectNumber: number) => void;
    navigate: (path: string) => void;
    projectAdminStaff: PagedResultSet<ProjectAdministratorStaff> | undefined;
    landman: Landman;
}

type StaffLandmanDialogState = {
    selectedClient: number;
    selectedProject: number;
    overrideRate: boolean;
};

class StaffLandmanDialog extends React.Component<IStaffLandmanDialogProps, StaffLandmanDialogState> {
    private form: FormApi;

    readonly state: Readonly<StaffLandmanDialogState> = {
        selectedClient: 0,
        selectedProject: 0,
        overrideRate: false,
    };

    changeClient = (clientNumber: number) => {
        this.form.change('projectId', undefined);
        this.form.change('projectAdminId', undefined);
        this.form.change('projectCoordinatorId', undefined);
        this.setState({ selectedClient: clientNumber, selectedProject: 0 });
        this.props.getProjects(clientNumber, new ODataOptions());
    };

    changeProject = (projectNumber: number) => {
        this.setState({ selectedProject: projectNumber });
        this.form.change('projectAdminId', undefined);
        this.form.change('projectCoordinatorId', undefined);
        this.props.getProjectAdministration(this.state.selectedClient, projectNumber);
    };

    overrideRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const overrideRate = event.target.value === 'true';
        this.setState({ overrideRate: overrideRate });
    };

    validate = (staffInfo: AnyObject) => {
        const errors: FormErrors<AnyObject> = {};

        if (staffInfo.clientId === undefined) {
            errors.clientId = 'Client name is required';
        }

        if (staffInfo.projectId === undefined) {
            errors.projectId = 'Project name is required';
        }

        return errors as FormErrors<AnyObject>;
    };

    goToProject = (clientId: number, projectId: number) => {
        this.props.navigate(`/clients/${clientId}/projects/${projectId}/staffing`);
    };

    handleStaffLandmanSubmit = (values: {
        projectId: number;
        userId: number;
        overrideRate: boolean;
        clientId: number;
        projectAdminId: number;
        projectCoordinatorId: number;
    }) => {
        const projectStaff = new ProjectStaff();
        projectStaff.user.id = this.props.landman.id;
        projectStaff.projectAdministratorId = values.projectAdminId;
        projectStaff.pcapmId = values.projectCoordinatorId;
        this.props.staffToProject(values.clientId, values.projectId, projectStaff);
    };

    public render() {
        const landman = PropertyUtil.SafeGet(() => this.props.landman);

        const clients = PropertyUtil.SafeGet(() => this.props.clients.items.map((item) => new SelectItem(item.clientNumber, item.cloakName)));

        const projects = PropertyUtil.SafeGet(() => this.props.projects.items.map((item) => new SelectItem(item.projectNumber, item.name)));

        const projectManagerStaff = PropertyUtil.SafeGet(() =>
            this.props.projectAdminStaff.items
                .filter((x) => x.projectAdministrationType.id === ProjectAdministratorTypes.ProspectManager)
                .map((item) => new SelectItem(item.id, `${item.user.firstName} ${item.user.lastName}`))
        );

        const projectCoordinatorStaff = PropertyUtil.SafeGet(() =>
            this.props.projectAdminStaff.items
                .filter((x) => x.projectAdministrationType.id === ProjectAdministratorTypes.ProjectCoordinator)
                .map((item) => new SelectItem(item.id, `${item.user.firstName} ${item.user.lastName}`))
        );

        return (
            <Dialog dialogName={DialogList.STAFF_LANDMAN} title={`Staff ${landman && landman.firstName + ' ' + landman.lastName} to project`}>
                <div className="content">
                    <Form onSubmit={this.handleStaffLandmanSubmit} onFormCreated={(form) => (this.form = form)} validate={this.validate}>
                        <div>
                            <div className="lh-half-width">
                                <div>
                                    <FormSelect
                                        label="Select Client"
                                        name="clientId"
                                        defaultOption="Select Client"
                                        onChange={(clientNumber: number) => this.changeClient(clientNumber)}
                                        items={clients}
                                    />
                                </div>
                            </div>
                            <div className="lh-half-width">
                                <div>
                                    <FormSelect
                                        label="Select Project"
                                        name="projectId"
                                        defaultOption="Select Project"
                                        onChange={this.changeProject}
                                        items={projects}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="details-panel">
                            <div className="lh-half-width">
                                <div>
                                    <FormSelect
                                        label="Project Administrator"
                                        name="projectAdminId"
                                        defaultOption="Choose Project Administrator"
                                        items={projectManagerStaff}
                                    />
                                </div>
                            </div>
                            <div className="lh-half-width">
                                <div>
                                    <FormSelect
                                        label="PCAPM"
                                        name="projectCoordinatorId"
                                        defaultOption="Choose PCAPM"
                                        items={projectCoordinatorStaff}
                                    />
                                </div>
                            </div>
                            <div>
                                <span>
                                    <b>{`Bill Rate: ${PropertyUtil.SafeGet(
                                        () => MathUtil.cleanNumber(landman.effectiveRate.billRate, 5),
                                        'NA',
                                        '${0} / day'
                                    )} `}</b>
                                    <b>{`Pay Rate: ${PropertyUtil.SafeGet(() => MathUtil.cleanNumber(landman.effectiveRate.payRate, 5), 'NA', '${0} / day')}`}</b>
                                </span>
                                <div style={{ marginTop: '.5em' }}>
                                    <FormCheckbox onChange={this.overrideRateChange} name="overrideRate" label="Override Rates" />
                                </div>
                            </div>
                            <div>
                                {this.form && this.form.getFieldState('overrideRate') && this.form.getFieldState('overrideRate').value && (
                                    <>
                                        <div className="lh-half-width">
                                            <div>
                                                <FormTextBox name="billRate" isDisabled={true} label="Bill Rate [disabled]" />
                                            </div>
                                        </div>
                                        <div className="lh-half-width">
                                            <div>
                                                <FormTextBox name="payRate" isDisabled={true} label="Pay Rate [disabled]" />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <Button type="submit" className="bp3-button bp3-intent-primary" text={'Save'} />
                        <Button
                            text={'Go to Project'}
                            className="bp3-button bp3-intent-minimal"
                            disabled={this.state.selectedClient === 0 || this.state.selectedProject === 0}
                            onClick={(e) => this.goToProject(this.state.selectedClient, this.state.selectedProject)}
                        />
                    </Form>
                </div>
            </Dialog>
        );
    }
}

export default StaffLandmanDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/StaffLandmanDialog.tsx